import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { orderService, returnService, extractErrorDetails } from '../services/api';
import CreateOrder from './CreateOrder';
import OrderList from './OrderList';
import OrderFilters from './OrderFilters';
import CreateReturn from './CreateReturn';
import ReturnList from './ReturnList';
import CreateSupplierOrder from './CreateSupplierOrder';
import SupplierOrderList from './SupplierOrderList';
import ProductManagement from './ProductManagement';
import ProductImport from './ProductImport';
import StockManagement from './StockManagement';
import BulkStockUpdate from './BulkStockUpdate';
import BatchStockUpdate from './BatchStockUpdate';
import BatchScheduleAdmin from './BatchScheduleAdmin';
import UserManagement from './UserManagement';
import SupermercadoManagement from './SupermercadoManagement';
import ProveedorManagement from './ProveedorManagement';
import LowStockReport from './LowStockReport';
import OrdersBySupermercadoReport from './OrdersBySupermercadoReport';
import StockMovementsReport from './StockMovementsReport';
import ProductFlowReport from './ProductFlowReport';
import ProductFrequencyReport from './ProductFrequencyReport';
import SupermercadoStockView from './SupermercadoStockView';
import SupermercadoStockReport from './SupermercadoStockReport';
import BatchOrderComparisonReport from './BatchOrderComparisonReport';
import LogViewer from './LogViewer';
import SqlConsole from './SqlConsole';
import DatabaseSchema from './DatabaseSchema';
import ErrorDisplay from './ErrorDisplay';

function Dashboard() {
  const { user, logout, debugMode } = useAuth();
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [useFilters, setUseFilters] = useState(false);
  const [useReturnFilters, setUseReturnFilters] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeReportTab, setActiveReportTab] = useState('low-stock');
  const [filters, setFilters] = useState({
    orderNumber: '',
    status: '',
    fromDate: '',
    toDate: '',
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  const [returnFilters, setReturnFilters] = useState({
    orderNumber: '',
    status: '',
    fromDate: '',
    toDate: '',
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0
  });
  const [returnPagination, setReturnPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0
  });

  const isDeposito = user?.role === 'ROLE_ADMIN_DEPOSITO';
  const isSupermercado = user?.role === 'ROLE_ADMIN_SUPERMERCADO';
  const isSistema = user?.role === 'ROLE_ADMIN_SISTEMA';

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      setDebugInfo(null);
      let response;
      if (isSupermercado) {
        response = await orderService.getMyOrders();
      } else {
        response = await orderService.getAllOrders();
      }
      if (response.success) {
        setOrders(response.data || []);
      } else {
        setError(response.message || 'Error al cargar las órdenes');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al cargar las órdenes');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoading(false);
    }
  }, [isSupermercado]);

  const fetchOrdersWithFilters = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      setDebugInfo(null);
      const filterParams = {
        ...filters,
        page: pagination.page,
        size: 20
      };
      
      // Clean empty values
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === '') {
          delete filterParams[key];
        }
      });
      
      let response;
      if (isSupermercado) {
        response = await orderService.getMyOrdersWithFilters(filterParams);
      } else {
        response = await orderService.getOrdersWithFilters(filterParams);
      }
      if (response.success) {
        const data = response.data;
        setOrders(data.content || []);
        setPagination({
          page: data.pageNumber,
          totalPages: data.totalPages,
          totalElements: data.totalElements
        });
      } else {
        setError(response.message || 'Error al cargar las órdenes');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al cargar las órdenes');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, isSupermercado]);

  const fetchReturns = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      setDebugInfo(null);
      let response;
      if (isSupermercado) {
        response = await returnService.getMyReturns();
      } else {
        response = await returnService.getAllReturns();
      }
      if (response.success) {
        setReturns(response.data || []);
      } else {
        setError(response.message || 'Error al cargar las devoluciones');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al cargar las devoluciones');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoading(false);
    }
  }, [isSupermercado]);

  const fetchReturnsWithFilters = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      setDebugInfo(null);
      const filterParams = {
        ...returnFilters,
        page: returnPagination.page,
        size: 20
      };
      
      // Clean empty values
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === '') {
          delete filterParams[key];
        }
      });
      
      let response;
      if (isSupermercado) {
        response = await returnService.getMyReturnsWithFilters(filterParams);
      } else {
        response = await returnService.getReturnsWithFilters(filterParams);
      }
      if (response.success) {
        const data = response.data;
        setReturns(data.content || []);
        setReturnPagination({
          page: data.pageNumber,
          totalPages: data.totalPages,
          totalElements: data.totalElements
        });
      } else {
        setError(response.message || 'Error al cargar las devoluciones');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al cargar las devoluciones');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoading(false);
    }
  }, [returnFilters, returnPagination.page, isSupermercado]);

  useEffect(() => {
    if (activeTab === 'orders' || isDeposito || isSupermercado) {
      if (useFilters) {
        fetchOrdersWithFilters();
      } else {
        fetchOrders();
      }
    }
    if (activeTab === 'returns') {
      if (useReturnFilters) {
        fetchReturnsWithFilters();
      } else {
        fetchReturns();
      }
    }
  }, [activeTab, isDeposito, isSupermercado, fetchOrders, fetchOrdersWithFilters, fetchReturns, fetchReturnsWithFilters, useFilters, useReturnFilters]);

  const handleOrderCreated = (newOrder) => {
    setOrders([newOrder, ...orders]);
    setActiveTab('orders');
  };

  const handleStatusUpdate = async (orderId, newStatus, confirmedItems = null) => {
    try {
      setError('');
      setErrorTrace(null);
      setDebugInfo(null);
      const response = await orderService.updateOrderStatus(orderId, newStatus, confirmedItems);
      if (response.success) {
        setOrders(orders.map(order => 
          order.id === orderId ? response.data : order
        ));
      } else {
        setError(response.message || 'Error al actualizar el estado');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al actualizar el estado');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    }
  };

  const handleReturnCreated = (newReturn) => {
    setReturns([newReturn, ...returns]);
    setActiveTab('returns');
  };

  const handleReturnStatusUpdate = async (returnId, newStatus, confirmedItems = null) => {
    try {
      setError('');
      setErrorTrace(null);
      setDebugInfo(null);
      const statusData = { 
        status: newStatus
      };
      if (confirmedItems) {
        statusData.confirmedItems = confirmedItems;
      }
      const response = await returnService.updateReturnStatus(returnId, statusData);
      if (response.success) {
        setReturns(returns.map(returnDoc => 
          returnDoc.id === returnId ? response.data : returnDoc
        ));
      } else {
        setError(response.message || 'Error al actualizar el estado');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al actualizar el estado');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    }
  };

  const handleApplyFilters = () => {
    setUseFilters(true);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      fromDate: '',
      toDate: '',
      sortBy: 'createdAt',
      sortDirection: 'desc'
    });
    setUseFilters(false);
    setPagination({ page: 0, totalPages: 0, totalElements: 0 });
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };
  
  const handleApplyReturnFilters = () => {
    setUseReturnFilters(true);
    setReturnPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleClearReturnFilters = () => {
    setReturnFilters({
      status: '',
      fromDate: '',
      toDate: '',
      sortBy: 'createdAt',
      sortDirection: 'desc'
    });
    setUseReturnFilters(false);
    setReturnPagination({ page: 0, totalPages: 0, totalElements: 0 });
  };

  const handleReturnPageChange = (newPage) => {
    setReturnPagination(prev => ({ ...prev, page: newPage }));
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'ROLE_ADMIN_DEPOSITO':
        return 'Administrador de Depósito';
      case 'ROLE_ADMIN_SUPERMERCADO':
        return 'Administrador de Supermercado';
      case 'ROLE_ADMIN_SISTEMA':
        return 'Administrador del Sistema';
      default:
        return role;
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMenuOpen(false);
    // Reset report tab to appropriate default when switching to reports
    if (tab === 'reports') {
      if (isSupermercado) {
        setActiveReportTab('product-frequency');
      } else if (isSistema) {
        setActiveReportTab('super-stock-report');
      } else {
        setActiveReportTab('low-stock');
      }
    }
  };

  const getNavItems = () => {
    if (isSistema) {
      return [
        { id: 'users', label: 'Usuarios' },
        { id: 'supermercados', label: 'Supermercados' },
        { id: 'batch-stock-update', label: 'Actualización Batch' },
        { id: 'batch-schedule', label: 'Horarios Batch' },
        { id: 'orders', label: 'Órdenes' },
        { id: 'returns', label: 'Devoluciones' },
        { id: 'reports', label: 'Reportes' },
        { id: 'logs', label: 'Logs' },
        { id: 'sql-console', label: 'Consola SQL' },
        { id: 'database-schema', label: 'Esquema BD' }
      ];
    }
    if (isDeposito) {
      return [
        { id: 'orders', label: 'Órdenes' },
        { id: 'returns', label: 'Devoluciones' },
        { id: 'supplier-orders', label: 'Órdenes a Proveedor' },
        { id: 'create-supplier-order', label: 'Crear Orden Proveedor' },
        { id: 'products', label: 'Productos' },
        { id: 'product-import', label: 'Importar Productos' },
        { id: 'proveedores', label: 'Proveedores' },
        { id: 'stock', label: 'Stock Depósito' },
        { id: 'bulk-stock-update', label: 'Actualización Masiva Stock' },
        { id: 'reports', label: 'Reportes' }
      ];
    }
    if (isSupermercado) {
      return [
        { id: 'orders', label: 'Mis Órdenes' },
        { id: 'create', label: 'Crear Orden' },
        { id: 'returns', label: 'Mis Devoluciones' },
        { id: 'create-return', label: 'Crear Devolución' },
        { id: 'super-stock', label: 'Mi Stock' },
        { id: 'reports', label: 'Reportes' }
      ];
    }
    return [];
  };

  const navItems = getNavItems();

  return (
    <div>
      <header className="header">
        <div className="header-left">
          <button 
            className="hamburger-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span className={`hamburger-icon ${menuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <h1>5 Esquinas</h1>
          {user?.supermercadoNombre && (
            <span className="header-supermercado">📍 {user.supermercadoNombre}</span>
          )}
        </div>
        <div className="header-info">
          <span className="user-name">{user?.nombre}</span>
          <span className="role-badge">{getRoleDisplay(user?.role)}</span>
          <button onClick={logout} className="btn btn-logout">Cerrar Sesión</button>
        </div>
      </header>

      {/* Mobile dropdown menu */}
      <nav className={`mobile-nav ${menuOpen ? 'open' : ''}`}>
        {navItems.map(item => (
          <button
            key={item.id}
            className={`mobile-nav-item ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => handleTabChange(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {/* Desktop navigation tabs */}
      <nav className="dashboard-nav desktop-nav">
        {navItems.map(item => (
          <button
            key={item.id}
            className={`nav-tab ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => handleTabChange(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div className="dashboard-content">
        {/* Sistema: Users tab */}
        {isSistema && activeTab === 'users' && (
          <div className="card">
            <UserManagement />
          </div>
        )}

        {/* Sistema: Supermercados tab */}
        {isSistema && activeTab === 'supermercados' && (
          <div className="card">
            <SupermercadoManagement />
          </div>
        )}

        {/* Sistema: Batch Stock Update tab */}
        {isSistema && activeTab === 'batch-stock-update' && (
          <div className="card">
            <BatchStockUpdate />
          </div>
        )}

        {/* Sistema: Batch Schedule Admin tab */}
        {isSistema && activeTab === 'batch-schedule' && (
          <div className="card">
            <BatchScheduleAdmin />
          </div>
        )}

        {/* Sistema: Orders tab (view only) */}
        {isSistema && activeTab === 'orders' && (
          <div className="card">
            <h2 className="page-title">Todas las Órdenes</h2>
            
            <OrderFilters
              filters={filters}
              onFilterChange={setFilters}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
            
            <div className="export-buttons" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <button
                onClick={async () => {
                  try {
                    await orderService.exportOrdersToExcel(filters);
                  } catch (err) {
                    console.error('Error exporting to Excel:', err);
                    const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a Excel');
                    setError(message);
                    setErrorTrace(trace);
                    setDebugInfo(debug);
                  }
                }}
                className="btn btn-small btn-success"
                style={{ marginRight: '10px' }}
              >
                📊 Exportar a Excel
              </button>
              <button
                onClick={async () => {
                  try {
                    await orderService.exportOrdersToCsv(filters);
                  } catch (err) {
                    console.error('Error exporting to CSV:', err);
                    const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a CSV');
                    setError(message);
                    setErrorTrace(trace);
                    setDebugInfo(debug);
                  }
                }}
                className="btn btn-small btn-info"
              >
                📋 Exportar a CSV
              </button>
            </div>
            
            <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
            
            {loading ? (
              <p>Cargando órdenes...</p>
            ) : (
              <>
                <OrderList 
                  orders={orders} 
                  isDeposito={false}
                  onStatusUpdate={handleStatusUpdate}
                />
                
                {useFilters && pagination.totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 0}
                      className="btn btn-small"
                    >
                      Anterior
                    </button>
                    <span>Página {pagination.page + 1} de {pagination.totalPages}</span>
                    <button 
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages - 1}
                      className="btn btn-small"
                    >
                      Siguiente
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Sistema: Returns tab (view only) */}
        {isSistema && activeTab === 'returns' && (
          <div className="card">
            <h2 className="page-title">Todas las Devoluciones</h2>
            
            <div className="export-buttons" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <button
                onClick={async () => {
                  try {
                    await returnService.exportReturnsToExcel(filters);
                  } catch (err) {
                    console.error('Error exporting to Excel:', err);
                    alert('Error al exportar a Excel');
                  }
                }}
                className="btn btn-small btn-success"
                style={{ marginRight: '10px' }}
              >
                📊 Exportar a Excel
              </button>
              <button
                onClick={async () => {
                  try {
                    await returnService.exportReturnsToCsv(filters);
                  } catch (err) {
                    console.error('Error exporting to CSV:', err);
                    alert('Error al exportar a CSV');
                  }
                }}
                className="btn btn-small btn-info"
              >
                📋 Exportar a CSV
              </button>
            </div>
            
            <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
            
            {loading ? (
              <p>Cargando devoluciones...</p>
            ) : (
              <ReturnList 
                returns={returns} 
                isDeposito={false}
                isSupermercado={false}
                onStatusUpdate={handleReturnStatusUpdate}
              />
            )}
          </div>
        )}

        {/* Deposito: Products tab */}
        {isDeposito && activeTab === 'products' && (
          <div className="card">
            <ProductManagement />
          </div>
        )}

        {/* Deposito: Product Import tab */}
        {isDeposito && activeTab === 'product-import' && (
          <div className="card">
            <ProductImport />
          </div>
        )}

        {/* Deposito: Proveedores tab */}
        {isDeposito && activeTab === 'proveedores' && (
          <div className="card">
            <ProveedorManagement />
          </div>
        )}

        {/* Deposito: Supplier Orders List tab */}
        {isDeposito && activeTab === 'supplier-orders' && (
          <div className="card">
            <SupplierOrderList />
          </div>
        )}

        {/* Deposito: Create Supplier Order tab */}
        {isDeposito && activeTab === 'create-supplier-order' && (
          <div className="card">
            <CreateSupplierOrder onOrderCreated={() => setActiveTab('supplier-orders')} />
          </div>
        )}

        {/* Deposito: Stock tab */}
        {isDeposito && activeTab === 'stock' && (
          <div className="card">
            <StockManagement />
          </div>
        )}

        {/* Deposito: Bulk Stock Update tab */}
        {isDeposito && activeTab === 'bulk-stock-update' && (
          <div className="card">
            <BulkStockUpdate />
          </div>
        )}

        {/* Deposito: Returns tab */}
        {isDeposito && activeTab === 'returns' && (
          <div className="card">
            <h2 className="page-title">Devoluciones de Supermercados</h2>
            
            <OrderFilters
              filters={returnFilters}
              onFilterChange={setReturnFilters}
              onApplyFilters={handleApplyReturnFilters}
              onClearFilters={handleClearReturnFilters}
            />
            
            <div className="export-buttons" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <button
                onClick={async () => {
                  try {
                    await returnService.exportReturnsToExcel(returnFilters);
                  } catch (err) {
                    console.error('Error exporting to Excel:', err);
                    alert('Error al exportar a Excel');
                  }
                }}
                className="btn btn-small btn-success"
                style={{ marginRight: '10px' }}
              >
                📊 Exportar a Excel
              </button>
              <button
                onClick={async () => {
                  try {
                    await returnService.exportReturnsToCsv(returnFilters);
                  } catch (err) {
                    console.error('Error exporting to CSV:', err);
                    alert('Error al exportar a CSV');
                  }
                }}
                className="btn btn-small btn-info"
              >
                📋 Exportar a CSV
              </button>
            </div>
            
            <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
            
            {loading ? (
              <p>Cargando devoluciones...</p>
            ) : (
              <>
                <ReturnList 
                  returns={returns} 
                  isDeposito={isDeposito}
                  isSupermercado={false}
                  onStatusUpdate={handleReturnStatusUpdate}
                />
                
                {useReturnFilters && returnPagination.totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      onClick={() => handleReturnPageChange(0)}
                      disabled={returnPagination.page === 0}
                      className="btn btn-sm"
                    >
                      {'<<'}
                    </button>
                    <button
                      onClick={() => handleReturnPageChange(returnPagination.page - 1)}
                      disabled={returnPagination.page === 0}
                      className="btn btn-sm"
                    >
                      {'<'}
                    </button>
                    <span>
                      Página {returnPagination.page + 1} de {returnPagination.totalPages}
                      {' '}({returnPagination.totalElements} total)
                    </span>
                    <button
                      onClick={() => handleReturnPageChange(returnPagination.page + 1)}
                      disabled={returnPagination.page >= returnPagination.totalPages - 1}
                      className="btn btn-sm"
                    >
                      {'>'}
                    </button>
                    <button
                      onClick={() => handleReturnPageChange(returnPagination.totalPages - 1)}
                      disabled={returnPagination.page >= returnPagination.totalPages - 1}
                      className="btn btn-sm"
                    >
                      {'>>'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Deposito: Orders tab */}
        {isDeposito && activeTab === 'orders' && (
          <div className="card">
            <h2 className="page-title">Órdenes de Supermercados</h2>
            
            <OrderFilters
              filters={filters}
              onFilterChange={setFilters}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
            
            <div className="export-buttons" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <button
                onClick={async () => {
                  try {
                    await orderService.exportOrdersToExcel(filters);
                  } catch (err) {
                    console.error('Error exporting to Excel:', err);
                    const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a Excel');
                    setError(message);
                    setErrorTrace(trace);
                    setDebugInfo(debug);
                  }
                }}
                className="btn btn-small btn-success"
                style={{ marginRight: '10px' }}
              >
                📊 Exportar a Excel
              </button>
              <button
                onClick={async () => {
                  try {
                    await orderService.exportOrdersToCsv(filters);
                  } catch (err) {
                    console.error('Error exporting to CSV:', err);
                    const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a CSV');
                    setError(message);
                    setErrorTrace(trace);
                    setDebugInfo(debug);
                  }
                }}
                className="btn btn-small btn-info"
              >
                📋 Exportar a CSV
              </button>
            </div>
            
            <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
            
            {loading ? (
              <p>Cargando órdenes...</p>
            ) : (
              <>
                <OrderList 
                  orders={orders} 
                  isDeposito={isDeposito}
                  onStatusUpdate={handleStatusUpdate}
                />
                
                {useFilters && pagination.totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 0}
                      className="btn btn-small"
                    >
                      Anterior
                    </button>
                    <span>Página {pagination.page + 1} de {pagination.totalPages}</span>
                    <button 
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages - 1}
                      className="btn btn-small"
                    >
                      Siguiente
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Supermercado: Orders tab */}
        {isSupermercado && activeTab === 'orders' && (
          <div className="card">
            <h2 className="page-title">Mis Órdenes</h2>
            
            <OrderFilters
              filters={filters}
              onFilterChange={setFilters}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
            
            <div className="export-buttons" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <button
                onClick={async () => {
                  try {
                    await orderService.exportMyOrdersToExcel(filters);
                  } catch (err) {
                    console.error('Error exporting to Excel:', err);
                    const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a Excel');
                    setError(message);
                    setErrorTrace(trace);
                    setDebugInfo(debug);
                  }
                }}
                className="btn btn-small btn-success"
                style={{ marginRight: '10px' }}
              >
                📊 Exportar a Excel
              </button>
              <button
                onClick={async () => {
                  try {
                    await orderService.exportMyOrdersToCsv(filters);
                  } catch (err) {
                    console.error('Error exporting to CSV:', err);
                    const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al exportar a CSV');
                    setError(message);
                    setErrorTrace(trace);
                    setDebugInfo(debug);
                  }
                }}
                className="btn btn-small btn-info"
              >
                📋 Exportar a CSV
              </button>
            </div>
            
            <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
            
            {loading ? (
              <p>Cargando órdenes...</p>
            ) : (
              <>
                <OrderList 
                  orders={orders} 
                  isDeposito={false}
                  isSupermercado={isSupermercado}
                  onStatusUpdate={handleStatusUpdate}
                />
                
                {useFilters && pagination.totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 0}
                      className="btn btn-small"
                    >
                      Anterior
                    </button>
                    <span>Página {pagination.page + 1} de {pagination.totalPages}</span>
                    <button 
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages - 1}
                      className="btn btn-small"
                    >
                      Siguiente
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Supermercado: Create Order tab */}
        {isSupermercado && activeTab === 'create' && (
          <div className="card">
            <h2 className="page-title">Crear Nueva Orden</h2>
            <CreateOrder onOrderCreated={handleOrderCreated} />
          </div>
        )}

        {/* Supermercado: Returns tab */}
        {isSupermercado && activeTab === 'returns' && (
          <div className="card">
            <h2 className="page-title">Mis Devoluciones</h2>
            
            <OrderFilters
              filters={returnFilters}
              onFilterChange={setReturnFilters}
              onApplyFilters={handleApplyReturnFilters}
              onClearFilters={handleClearReturnFilters}
            />
            
            <div className="export-buttons" style={{ marginTop: '10px', marginBottom: '10px' }}>
              <button
                onClick={async () => {
                  try {
                    await returnService.exportMyReturnsToExcel(returnFilters);
                  } catch (err) {
                    console.error('Error exporting to Excel:', err);
                    alert('Error al exportar a Excel');
                  }
                }}
                className="btn btn-small btn-success"
                style={{ marginRight: '10px' }}
              >
                📊 Exportar a Excel
              </button>
              <button
                onClick={async () => {
                  try {
                    await returnService.exportMyReturnsToCsv(returnFilters);
                  } catch (err) {
                    console.error('Error exporting to CSV:', err);
                    alert('Error al exportar a CSV');
                  }
                }}
                className="btn btn-small btn-info"
              >
                📋 Exportar a CSV
              </button>
            </div>
            
            <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
            
            {loading ? (
              <p>Cargando devoluciones...</p>
            ) : (
              <>
                <ReturnList 
                  returns={returns} 
                  isDeposito={false}
                  isSupermercado={isSupermercado}
                  onStatusUpdate={handleReturnStatusUpdate}
                />
                
                {useReturnFilters && returnPagination.totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      onClick={() => handleReturnPageChange(0)}
                      disabled={returnPagination.page === 0}
                      className="btn btn-sm"
                    >
                      {'<<'}
                    </button>
                    <button
                      onClick={() => handleReturnPageChange(returnPagination.page - 1)}
                      disabled={returnPagination.page === 0}
                      className="btn btn-sm"
                    >
                      {'<'}
                    </button>
                    <span>
                      Página {returnPagination.page + 1} de {returnPagination.totalPages}
                      {' '}({returnPagination.totalElements} total)
                    </span>
                    <button
                      onClick={() => handleReturnPageChange(returnPagination.page + 1)}
                      disabled={returnPagination.page >= returnPagination.totalPages - 1}
                      className="btn btn-sm"
                    >
                      {'>'}
                    </button>
                    <button
                      onClick={() => handleReturnPageChange(returnPagination.totalPages - 1)}
                      disabled={returnPagination.page >= returnPagination.totalPages - 1}
                      className="btn btn-sm"
                    >
                      {'>>'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Supermercado: Create Return tab */}
        {isSupermercado && activeTab === 'create-return' && (
          <div className="card">
            <h2 className="page-title">Crear Nueva Devolución</h2>
            <CreateReturn 
              onReturnCreated={handleReturnCreated} 
              userSupermercadoId={user?.supermercadoId}
            />
          </div>
        )}

        {/* Supermercado: Stock View tab */}
        {isSupermercado && activeTab === 'super-stock' && (
          <div className="card">
            <SupermercadoStockView />
          </div>
        )}

        {/* Deposito: Reports tab */}
        {isDeposito && activeTab === 'reports' && (
          <div className="card">
            <h2 className="page-title">Reportes del Depósito</h2>
            
            <div className="report-tabs">
              <button 
                className={`report-tab ${activeReportTab === 'low-stock' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('low-stock')}
              >
                Bajo Stock
              </button>
              <button 
                className={`report-tab ${activeReportTab === 'orders-by-supermercado' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('orders-by-supermercado')}
              >
                Órdenes por Supermercado
              </button>
              <button 
                className={`report-tab ${activeReportTab === 'stock-movements' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('stock-movements')}
              >
                Movimientos de Stock
              </button>
              <button 
                className={`report-tab ${activeReportTab === 'product-flow' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('product-flow')}
              >
                Flujo de Producto
              </button>
              <button 
                className={`report-tab ${activeReportTab === 'batch-order-comparison' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('batch-order-comparison')}
              >
                Comparación Batch vs Órdenes
              </button>
              <button 
                className={`report-tab ${activeReportTab === 'super-stock-report' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('super-stock-report')}
              >
                Stock Supermercados
              </button>
            </div>

            {activeReportTab === 'low-stock' && <LowStockReport />}
            {activeReportTab === 'orders-by-supermercado' && <OrdersBySupermercadoReport />}
            {activeReportTab === 'stock-movements' && <StockMovementsReport />}
            {activeReportTab === 'product-flow' && <ProductFlowReport />}
            {activeReportTab === 'batch-order-comparison' && <BatchOrderComparisonReport />}
            {activeReportTab === 'super-stock-report' && <SupermercadoStockReport />}
          </div>
        )}

        {/* Sistema: Reports tab */}
        {isSistema && activeTab === 'reports' && (
          <div className="card">
            <h2 className="page-title">Reportes del Sistema</h2>
            
            <div className="report-tabs">
              <button 
                className={`report-tab ${activeReportTab === 'super-stock-report' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('super-stock-report')}
              >
                Stock Supermercados
              </button>
            </div>

            {activeReportTab === 'super-stock-report' && <SupermercadoStockReport />}
          </div>
        )}

        {/* Sistema: Logs tab */}
        {isSistema && activeTab === 'logs' && (
          <div className="card">
            <LogViewer />
          </div>
        )}

        {/* Sistema: SQL Console tab */}
        {isSistema && activeTab === 'sql-console' && (
          <div className="card">
            <SqlConsole />
          </div>
        )}

        {/* Sistema: Database Schema tab */}
        {isSistema && activeTab === 'database-schema' && (
          <div className="card">
            <DatabaseSchema />
          </div>
        )}

        {/* Supermercado: Reports tab */}
        {isSupermercado && activeTab === 'reports' && (
          <div className="card">
            <h2 className="page-title">Reportes del Supermercado</h2>
            
            <div className="report-tabs">
              <button 
                className={`report-tab ${activeReportTab === 'product-frequency' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('product-frequency')}
              >
                Frecuencia de Pedidos
              </button>
              <button 
                className={`report-tab ${activeReportTab === 'super-stock-report' ? 'active' : ''}`}
                onClick={() => setActiveReportTab('super-stock-report')}
              >
                Stock del Supermercado
              </button>
            </div>

            {activeReportTab === 'product-frequency' && <ProductFrequencyReport />}
            {activeReportTab === 'super-stock-report' && <SupermercadoStockReport />}
          </div>
        )}
      </div>
      
      {/* Debug Mode Indicator */}
      {debugMode && (
        <div className="debug-mode-indicator">
          🔧 Modo Prueba Activo
        </div>
      )}
    </div>
  );
}

export default Dashboard;
